import { useState, useEffect } from 'react'
import { adsApi, type Advertiser, type Campaign, type RevenueShare } from '../../api/ads'
import { channelsApi } from '../../api/channels'
import { useUiStore } from '../../stores/uiStore'
import { Select } from '../ui/Select'

interface Props {
  onBack: () => void
}

type View = 'overview' | 'new-campaign' | 'monetize' | 'transactions' | { campaign: Campaign }

const STATUS_STYLE: Record<string, string> = {
  draft:          'text-txt-meta bg-bg-hover',
  pending_review: 'text-yellow-400 bg-yellow-400/10',
  approved:       'text-blue-400 bg-blue-400/10',
  rejected:       'text-red-400 bg-red-400/10',
  active:         'text-green-400 bg-green-400/10',
  paused:         'text-orange-400 bg-orange-400/10',
  completed:      'text-txt-meta bg-bg-hover',
  cancelled:      'text-red-300 bg-red-300/10',
}

function formatCents(cents: number) {
  return `$${(cents / 100).toFixed(2)}`
}

function RegisterForm({ onRegistered }: { onRegistered: (a: Advertiser) => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const { addToast } = useUiStore()

  const handleRegister = async () => {
    if (!name.trim() || !email.trim()) return
    setLoading(true)
    try {
      const adv = await adsApi.registerAdvertiser(name.trim(), email.trim())
      onRegistered(adv)
    } catch {
      addToast('error', 'Не удалось создать аккаунт рекламодателя')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-4 space-y-3">
      <div className="bg-accent/10 rounded-2xl p-4 text-center">
        <p className="text-3xl mb-2">📢</p>
        <p className="text-txt-primary font-semibold">Create Advertiser Account</p>
        <p className="text-txt-meta text-xs mt-1">Reach millions of users with targeted ads</p>
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Компания / Имя"
        className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email для счетов"
        className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
      />
      <button
        onClick={handleRegister}
        disabled={loading || !name.trim() || !email.trim()}
        className="w-full bg-accent hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
      >
        {loading ? 'Создание…' : 'Создать аккаунт'}
      </button>
    </div>
  )
}

function NewCampaignForm({ onBack, onCreate }: { onBack: () => void; onCreate: (c: Campaign) => void }) {
  const [form, setForm] = useState({
    name: '',
    objective: 'awareness',
    budget_cents: 1000,
    daily_budget_cents: 100,
    cpm_bid_millicents: 5000,
  })
  const [loading, setLoading] = useState(false)
  const { addToast } = useUiStore()

  const handleCreate = async () => {
    if (!form.name.trim()) return
    setLoading(true)
    try {
      const c = await adsApi.createCampaign({
        name: form.name.trim(),
        objective: form.objective,
        budget_cents: Math.round(form.budget_cents * 100),
        daily_budget_cents: Math.round(form.daily_budget_cents * 100),
        cpm_bid_millicents: form.cpm_bid_millicents,
      })
      addToast('success', 'Кампания создана!')
      onCreate(c)
    } catch {
      addToast('error', 'Не удалось создать кампанию')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary text-sm">← Back</button>
        <p className="text-txt-primary font-semibold">New Campaign</p>
      </div>
      <div>
        <label className="text-txt-meta text-xs">Campaign name</label>
        <input
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Моя кампания"
          className="mt-1 w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
        />
      </div>
      <div>
        <label className="text-txt-meta text-xs">Objective</label>
        <Select
          value={form.objective}
          onChange={(v) => setForm((f) => ({ ...f, objective: v }))}
          className="mt-1 w-full"
          options={[
            { value: 'awareness', label: 'Узнаваемость бренда' },
            { value: 'traffic', label: 'Трафик' },
            { value: 'conversions', label: 'Конверсии' },
            { value: 'installs', label: 'Установки приложения' },
          ]}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-txt-meta text-xs">Total budget ($)</label>
          <input
            type="number"
            min="1"
            value={form.budget_cents}
            onChange={(e) => setForm((f) => ({ ...f, budget_cents: Number(e.target.value) }))}
            className="mt-1 w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div>
          <label className="text-txt-meta text-xs">Daily budget ($)</label>
          <input
            type="number"
            min="1"
            value={form.daily_budget_cents}
            onChange={(e) => setForm((f) => ({ ...f, daily_budget_cents: Number(e.target.value) }))}
            className="mt-1 w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      </div>
      <button
        onClick={handleCreate}
        disabled={loading || !form.name.trim()}
        className="w-full bg-accent hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
      >
        {loading ? 'Создание…' : 'Создать кампанию'}
      </button>
    </div>
  )
}

function CampaignDetail({ campaign, onBack, onUpdate }: { campaign: Campaign; onBack: () => void; onUpdate: (c: Campaign) => void }) {
  const [report, setReport] = useState<{ impressions: number; clicks: number; ctr: number; spent_cents: number } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [pausing, setPausing] = useState(false)
  const [activating, setActivating] = useState(false)
  const [showCreativeForm, setShowCreativeForm] = useState(false)
  const [creativeForm, setCreativeForm] = useState({ title: '', body: '', cta_url: '', cta_label: 'Подробнее' })
  const [savingCreative, setSavingCreative] = useState(false)
  const { addToast } = useUiStore()

  useEffect(() => {
    adsApi.getCampaignReport(campaign.id)
      .then((r) => setReport(r))
      .catch(() => {})
  }, [campaign.id])

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const updated = await adsApi.submitCampaign(campaign.id)
      onUpdate(updated)
      addToast('success', 'Кампания отправлена на проверку')
    } catch {
      addToast('error', 'Не удалось отправить кампанию')
    } finally {
      setSubmitting(false)
    }
  }

  const handleActivate = async () => {
    setActivating(true)
    try {
      const updated = await adsApi.activateCampaign(campaign.id)
      onUpdate(updated)
      addToast('success', 'Кампания активирована!')
    } catch {
      addToast('error', 'Не удалось активировать кампанию')
    } finally {
      setActivating(false)
    }
  }

  const handlePause = async () => {
    setPausing(true)
    try {
      await adsApi.pauseCampaign(campaign.id)
      onUpdate({ ...campaign, status: 'paused' })
      addToast('success', 'Кампания приостановлена')
    } catch {
      addToast('error', 'Не удалось приостановить кампанию')
    } finally {
      setPausing(false)
    }
  }

  const handleCreateCreative = async () => {
    if (!creativeForm.title.trim() || !creativeForm.body.trim() || !creativeForm.cta_url.trim()) return
    setSavingCreative(true)
    try {
      await adsApi.createCreative(campaign.id, {
        title: creativeForm.title.trim(),
        body: creativeForm.body.trim(),
        cta_url: creativeForm.cta_url.trim(),
        cta_label: creativeForm.cta_label.trim() || 'Learn More',
      })
      addToast('success', 'Креатив создан!')
      setShowCreativeForm(false)
      setCreativeForm({ title: '', body: '', cta_url: '', cta_label: 'Подробнее' })
    } catch {
      addToast('error', 'Не удалось создать креатив')
    } finally {
      setSavingCreative(false)
    }
  }

  const style = STATUS_STYLE[campaign.status] ?? STATUS_STYLE.draft

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary text-sm">← Back</button>
        <p className="text-txt-primary font-semibold flex-1 truncate">{campaign.name}</p>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${style}`}>{campaign.status}</span>
      </div>

      <div className="bg-bg-input rounded-2xl p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-txt-meta">Budget</span>
          <span className="text-txt-primary">{formatCents(campaign.budget_cents)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-txt-meta">Spent</span>
          <span className="text-txt-primary">{formatCents(campaign.spent_cents)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-txt-meta">Daily budget</span>
          <span className="text-txt-primary">{formatCents(campaign.daily_budget_cents)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-txt-meta">Objective</span>
          <span className="text-txt-primary capitalize">{campaign.objective}</span>
        </div>
      </div>

      {report && (
        <div className="bg-bg-input rounded-2xl p-4">
          <p className="text-txt-meta text-xs uppercase tracking-wider font-medium mb-2">Performance</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-txt-primary font-semibold text-base">{report.impressions.toLocaleString()}</p>
              <p className="text-txt-meta text-xs">Impressions</p>
            </div>
            <div>
              <p className="text-txt-primary font-semibold text-base">{report.clicks.toLocaleString()}</p>
              <p className="text-txt-meta text-xs">Clicks</p>
            </div>
            <div>
              <p className="text-txt-primary font-semibold text-base">{(report.ctr * 100).toFixed(2)}%</p>
              <p className="text-txt-meta text-xs">CTR</p>
            </div>
          </div>
        </div>
      )}

      {campaign.review_note && (
        <div className="bg-red-500/10 rounded-2xl p-3 text-red-400 text-sm">
          <p className="font-medium text-xs mb-1">Review note</p>
          {campaign.review_note}
        </div>
      )}

      <div className="flex gap-2">
        {campaign.status === 'draft' && (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-accent hover:bg-blue-500 text-white py-2 rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
          >
            {submitting ? 'Отправка…' : '📤 На проверку'}
          </button>
        )}
        {campaign.status === 'approved' && (
          <button
            onClick={handleActivate}
            disabled={activating}
            className="flex-1 bg-green-500 hover:bg-green-400 text-white py-2 rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
          >
            {activating ? '…' : '▶ Activate Campaign'}
          </button>
        )}
        {campaign.status === 'active' && (
          <button
            onClick={handlePause}
            disabled={pausing}
            className="flex-1 bg-orange-500/80 hover:bg-orange-500 text-white py-2 rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
          >
            {pausing ? '…' : '⏸ Pause'}
          </button>
        )}
      </div>

      {/* Creative form */}
      {['draft', 'approved', 'active'].includes(campaign.status) && (
        <div className="border-t border-divider pt-3">
          {!showCreativeForm ? (
            <button
              onClick={() => setShowCreativeForm(true)}
              className="w-full text-left bg-bg-input hover:bg-bg-hover rounded-xl px-4 py-2.5 text-sm text-txt-secondary transition-colors flex items-center justify-between"
            >
              <span>+ Add Creative (ad copy)</span>
              <span className="text-txt-meta text-xs">→</span>
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-txt-meta text-xs uppercase tracking-wider font-medium">New Creative</p>
              <input
                value={creativeForm.title}
                onChange={(e) => setCreativeForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Заголовок"
                className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
              />
              <textarea
                value={creativeForm.body}
                onChange={(e) => setCreativeForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="Текст рекламы"
                rows={2}
                className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent resize-none placeholder-txt-meta"
              />
              <input
                value={creativeForm.cta_url}
                onChange={(e) => setCreativeForm((f) => ({ ...f, cta_url: e.target.value }))}
                placeholder="CTA URL (https://…)"
                type="url"
                className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
              />
              <input
                value={creativeForm.cta_label}
                onChange={(e) => setCreativeForm((f) => ({ ...f, cta_label: e.target.value }))}
                placeholder="Текст кнопки"
                className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCreateCreative}
                  disabled={savingCreative || !creativeForm.title.trim() || !creativeForm.body.trim() || !creativeForm.cta_url.trim()}
                  className="flex-1 bg-accent hover:bg-blue-500 text-white py-2 rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
                >
                  {savingCreative ? 'Сохранение…' : 'Создать'}
                </button>
                <button onClick={() => setShowCreativeForm(false)}
                  className="bg-bg-input text-txt-secondary px-3 py-2 rounded-xl text-sm hover:bg-bg-hover transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function TransactionsView({ onBack }: { onBack: () => void }) {
  const [txs, setTxs] = useState<Array<{ id: string; type: string; amount_cents: number; description: string; created_at: string }>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    adsApi.listAdTransactions()
      .then((r) => setTxs(r.transactions ?? []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary text-sm">← Back</button>
        <p className="text-txt-primary font-semibold">История транзакций</p>
      </div>
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
        </div>
      ) : txs.length === 0 ? (
        <p className="text-txt-meta text-sm text-center py-8">No transactions yet</p>
      ) : (
        <div className="space-y-2">
          {txs.map((tx) => (
            <div key={tx.id} className="bg-bg-input rounded-xl px-4 py-3 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-txt-primary text-sm truncate">{tx.description || tx.type}</p>
                <p className="text-txt-meta text-xs">{new Date(tx.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</p>
              </div>
              <span className={`text-sm font-semibold shrink-0 ml-3 ${tx.amount_cents >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {tx.amount_cents >= 0 ? '+' : ''}{formatCents(tx.amount_cents)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function MonetizeView({ onBack }: { onBack: () => void }) {
  const [channels, setChannels] = useState<Array<{ id: string; name: string }>>([])
  const [revenues, setRevenues] = useState<RevenueShare[]>([])
  const [registering, setRegistering] = useState(false)
  const [selectedChannel, setSelectedChannel] = useState('')
  const [category, setCategory] = useState('general')
  const { addToast } = useUiStore()

  useEffect(() => {
    channelsApi.listSubscriptions()
      .then((r) => setChannels(r.channels ?? []))
      .catch(() => {})
    adsApi.getMyRevenueShares()
      .then((r) => setRevenues(r.revenue_shares ?? []))
      .catch(() => {})
  }, [])

  const handleRegister = async () => {
    if (!selectedChannel) return
    const ch = channels.find((c) => c.id === selectedChannel)
    setRegistering(true)
    try {
      await adsApi.registerPlacement({
        channel_id: selectedChannel,
        display_name: ch?.name ?? selectedChannel,
        category,
      })
      addToast('success', 'Канал подключён к монетизации!')
    } catch {
      addToast('error', 'Не удалось зарегистрировать канал')
    } finally {
      setRegistering(false)
    }
  }

  const totalEarned = revenues.reduce((a, r) => a + r.share_cents, 0)

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary text-sm">← Back</button>
        <p className="text-txt-primary font-semibold">Channel Monetization</p>
      </div>

      {totalEarned > 0 && (
        <div className="bg-gradient-to-br from-green-500/20 to-emerald-600/10 rounded-2xl p-4 border border-green-500/20 text-center">
          <p className="text-txt-meta text-xs">Total earned</p>
          <p className="text-txt-primary text-2xl font-bold">${(totalEarned / 100).toFixed(2)}</p>
        </div>
      )}

      <div className="bg-bg-input rounded-2xl p-4 space-y-3">
        <p className="text-txt-secondary text-sm font-medium">Register a channel</p>
        <Select
          value={selectedChannel}
          onChange={setSelectedChannel}
          className="w-full"
          placeholder="Select channel…"
          options={channels.map((c) => ({ value: c.id, label: c.name }))}
        />
        <Select
          value={category}
          onChange={setCategory}
          className="w-full"
          options={['general', 'technology', 'news', 'entertainment', 'sports', 'education', 'business']
            .map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }))}
        />
        <button
          onClick={handleRegister}
          disabled={registering || !selectedChannel}
          className="w-full bg-green-500 hover:bg-green-400 text-white py-2 rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
        >
          {registering ? 'Registering…' : 'Enable Monetization'}
        </button>
      </div>

      {revenues.length > 0 && (
        <div>
          <p className="text-txt-meta text-xs uppercase tracking-wider font-medium mb-2">Revenue history</p>
          <div className="space-y-2">
            {revenues.slice(0, 10).map((r) => (
              <div key={r.id} className="bg-bg-input rounded-xl px-4 py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-txt-primary text-sm">{new Date(r.period_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })}</p>
                  <p className="text-txt-meta text-xs">{r.impressions.toLocaleString()} impressions</p>
                </div>
                <div className="text-right">
                  <p className="text-green-400 text-sm font-medium">${(r.share_cents / 100).toFixed(2)}</p>
                  {r.paid_at && <p className="text-txt-meta text-xs">Paid</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function AdsManagerPanel({ onBack }: Props) {
  const [advertiser, setAdvertiser] = useState<Advertiser | null | 'not-registered'>('not-registered')
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [view, setView] = useState<View>('overview')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    adsApi.getMyAdvertiser()
      .then((a) => setAdvertiser(a))
      .catch(() => setAdvertiser('not-registered'))
      .finally(() => setLoading(false))
  }, [])

  const content = () => {
    if (loading) return (
      <div className="flex justify-center py-8">
        <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    )

    if (advertiser === 'not-registered') {
      return <RegisterForm onRegistered={(a) => { setAdvertiser(a); setView('overview') }} />
    }

    if (view === 'monetize') {
      return <MonetizeView onBack={() => setView('overview')} />
    }

    if (view === 'transactions') {
      return <TransactionsView onBack={() => setView('overview')} />
    }

    if (view === 'new-campaign') {
      return (
        <NewCampaignForm
          onBack={() => setView('overview')}
          onCreate={(c) => { setCampaigns((prev) => [c, ...prev]); setView({ campaign: c }) }}
        />
      )
    }

    if (typeof view === 'object') {
      return (
        <CampaignDetail
          campaign={view.campaign}
          onBack={() => setView('overview')}
          onUpdate={(updated) => {
            setCampaigns((prev) => prev.map((c) => c.id === updated.id ? updated : c))
            setView({ campaign: updated })
          }}
        />
      )
    }

    // Overview
    const adv = advertiser as Advertiser
    return (
      <div className="p-4 space-y-4">
        {/* Balance card */}
        <div className="bg-gradient-to-br from-accent/20 to-blue-600/10 rounded-2xl p-4 border border-accent/20">
          <p className="text-txt-meta text-xs">Advertiser balance</p>
          <p className="text-txt-primary text-2xl font-bold">{formatCents(adv.balance_cents)}</p>
          <p className="text-txt-meta text-xs mt-1">{adv.name}</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setView('new-campaign')}
            className="flex-1 bg-accent hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
          >
            + New Campaign
          </button>
          <button
            onClick={() => setView('monetize')}
            className="flex-1 bg-green-500/80 hover:bg-green-500 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
          >
            💰 Monetize
          </button>
        </div>
        <button
          onClick={() => setView('transactions')}
          className="w-full text-left bg-bg-input hover:bg-bg-hover rounded-xl px-4 py-2.5 text-sm text-txt-secondary transition-colors flex items-center justify-between"
        >
          <span>История транзакций</span>
          <span className="text-txt-meta text-xs">→</span>
        </button>

        {campaigns.length === 0 ? (
          <p className="text-txt-meta text-sm text-center py-6">No campaigns yet</p>
        ) : (
          <div className="space-y-2">
            <p className="text-txt-meta text-xs uppercase tracking-wider font-medium">Campaigns</p>
            {campaigns.map((c) => (
              <button
                key={c.id}
                onClick={() => setView({ campaign: c })}
                className="w-full text-left bg-bg-input rounded-2xl px-4 py-3 hover:bg-bg-hover transition-colors flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-txt-primary text-sm font-medium truncate">{c.name}</p>
                  <p className="text-txt-meta text-xs">{formatCents(c.spent_cents)} spent of {formatCents(c.budget_cents)}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${STATUS_STYLE[c.status] ?? STATUS_STYLE.draft}`}>
                  {c.status}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">Ads Manager</h2>
      </div>
      <div className="flex-1 overflow-y-auto">
        {content()}
      </div>
    </div>
  )
}
